// services/authService.js
//
// ┌─────────────────────────────────────────────────────────────────┐
// │  THE ONE DOOR FOR AUTHENTICATION                                  │
// │                                                                   │
// │  This is the ONLY file in the app that talks to the auth backend  │
// │  (currently Firebase Auth). Every login / logout / sign-up in the │
// │  app goes through the functions here.                             │
// │                                                                   │
// │  ➜ To swap Firebase for Apple internal SSO later, you only need   │
// │    to rewrite the insides of THIS file. The rest of the app       │
// │    (app.js, tog.js) never needs to change.                        │
// └─────────────────────────────────────────────────────────────────┘

import {
    auth,
    loadFirebaseModules,
    onAuthStateChanged,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
} from './firebase.js';

// Make sure the auth backend is loaded before we use it.
// (Firebase modules are loaded lazily; this guarantees they're ready.)
export async function initAuthBackend() {
    await loadFirebaseModules();
}

// --- Reading the current user ---------------------------------------------

// Returns the currently signed-in user object (or null if signed out).
export function getCurrentUser() {
    return auth?.currentUser || null;
}

// Returns the raw auth instance. Used only where another module (tog.js)
// needs to be handed the backend directly. Kept here so this file stays
// the single source of truth for auth.
export function getAuthInstance() {
    return auth;
}

// --- Listening for login / logout -----------------------------------------

// Calls `callback(user)` whenever the user signs in or out.
// `user` is the user object when signed in, or null when signed out.
export function onAuthChange(callback) {
    return onAuthStateChanged(auth, callback);
}

// --- Sign-in / sign-up / sign-out actions ---------------------------------

// Sign in via Single Sign-On (currently Google; swap to Apple SSO later).
// Returns the signed-in user.
export async function signInWithSSO() {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    return result.user;
}

// Sign in with email + password. Returns the signed-in user.
export async function signInWithEmail(email, password) {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    return credential.user;
}

// Create a new account with email + password. Returns the new user.
export async function signUpWithEmail(email, password) {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    return credential.user;
}

// Send a password-reset email.
export async function sendPasswordReset(email) {
    await sendPasswordResetEmail(auth, email);
}

// Sign the current user out.
export async function logOut() {
    await signOut(auth);
}
